import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import EnhancedMainLayout from "@/components/Layout/MainLayout/EnhancedMainLayout";
import EnhancedSidebar from "@/components/Sidebar/EnhancedSidebar";
import InfoPanel from "@/components/InfoPanel";
import ChatInterface from "@/modules/chat/ChatInterface/ChatInterface";
import EnhancedSettingsPanel from "@/modules/chat/SettingsPanel/EnhancedSettingsPanel";
import { settingsService } from "@/services/SettingsService";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useConfig } from "@/context/ConfigContext";
import { apiInterceptor } from "@/services/ApiInterceptor";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

interface IndexProps {
  pageRoute?: string;
}

const Index: React.FC<IndexProps> = ({ pageRoute: propPageRoute }) => {
  const { pageRoute: paramPageRoute } = useParams<{ pageRoute: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPageRoute } = useAppConfig();
  const { config } = useConfig();

  // Use document title hook to update page title based on route
  useDocumentTitle();

  // Extract page route from location pathname (remove leading slash)
  const pathPageRoute =
    location.pathname === "/" ? "home" : location.pathname.substring(1);

  // Use prop if provided, otherwise use path, fallback to 'home'
  const currentPageRoute =
    propPageRoute || paramPageRoute || pathPageRoute || "home";

  const [showSettings, setShowSettings] = useState(false);
  const [zenMode, setZenMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Load zen mode state on mount
  useEffect(() => {
    const settings = settingsService.getSettings();
    setZenMode(settings.ui.zenMode);
  }, []);

  // Handle navigation logic for invalid routes
  useEffect(() => {
    // Don't redirect if config is not loaded yet
    if (!config || Object.keys(config.pages || {}).length === 0) {
      return;
    }

    // Prevent infinite redirect loops
    if (isRedirecting) {
      return;
    }
    
    // Only redirect if we're not already on /home and either:
    // 1. We're at root path (/)
    // 2. The page route doesn't exist in config
    if (
      location.pathname !== "/home" &&
      (location.pathname === "/" || !hasPageRoute(currentPageRoute))
    ) {
      setIsRedirecting(true);
      navigate("/home", { replace: true });
      return;
    }
  }, [
    currentPageRoute,
    location.pathname,
    hasPageRoute,
    navigate,
    isRedirecting,
    config,
  ]);

  // Reset redirecting state when we reach a valid route
  useEffect(() => {
    if (
      isRedirecting &&
      config &&
      hasPageRoute(currentPageRoute) &&
      location.pathname === "/home"
    ) {
      setIsRedirecting(false);
    }
  }, [
    isRedirecting,
    config,
    hasPageRoute,
    currentPageRoute,
    location.pathname,
  ]);

  // Timeout mechanism to prevent getting stuck in redirecting state
  useEffect(() => {
    if (isRedirecting) {
      const timeout = setTimeout(() => {
        setIsRedirecting(false);
      }, 3000); // 3 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isRedirecting]);

  // Cleanup redirecting state on unmount
  useEffect(() => {
    return () => {
      setIsRedirecting(false);
    };
  }, []);

  // Set page route in ApiInterceptor when it changes
  useEffect(() => {
    apiInterceptor.setPageRoute(currentPageRoute);
  }, [currentPageRoute]);

  const handleToggleZenMode = () => {
    const newZenMode = !zenMode;
    setZenMode(newZenMode);
    
    // Save to settings
    settingsService.saveSettings({
      ui: {
        ...settingsService.getSettings().ui,
        zenMode: newZenMode,
      },
    });
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Show loading while redirecting
  if (isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <EnhancedMainLayout 
      sidebar={
        <EnhancedSidebar 
          onSettingsClick={() => setShowSettings(true)}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          pageRoute={currentPageRoute}
        />
      } 
      infoPanel={<InfoPanel pageRoute={currentPageRoute} />}
      zenMode={zenMode}
      onToggleZenMode={handleToggleZenMode}
      enableSidebarCollapse={true}
    >
      <ChatInterface 
        zenMode={zenMode}
        onToggleZenMode={handleToggleZenMode}
        pageRoute={currentPageRoute}
      />
      <EnhancedSettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </EnhancedMainLayout>
  );
};

export default Index;
